import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';

import { OptionStatus, ParticipantMarketType, ParticipantType } from '@cds/betting-offer';
import { EventOptionGroup, Tv2MarketParameters } from '@frontend/sports/betting-offer/feature/model';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import {
    ForecastParticipantViewModel,
    MarketView,
    RacingOptionMarketTypes,
    WinGameFixture,
    WinGameParticipant,
} from '@frontend/sports/racing/feature/models';
import { cloneDeep } from 'lodash-es';
import { ParticipantPickType } from 'packages/sports/common/betslip/core/picks/pick-models';

import { HorsesFixtureMarketHelper } from '../../../../horse-racing-shared/services/horses-fixture-market-helper.service';
import { HorsesFixtureParticipantHelper } from '../../../../horse-racing-shared/services/horses-fixture-participant-helper.service';
import { HorsesFixturePricingHelper } from '../../../../horse-racing-shared/services/horses-fixture-pricing-helper.service';
import { RacingService } from '../../../services/racing-service';
import { GreyhoundsPostPicksService } from './greyhounds-post-picks.service';

@Component({
    template: '',
    standalone: false,
})
export class RacingPostBaseComponent implements OnChanges {
    @Input() fixture: WinGameFixture;
    @Input() trackingSource = 'racingpost-123';
    @Input() optionGroup?: EventOptionGroup;
    @Input() isSports: boolean;
    @Input() isGreyhounds: boolean;

    @Output() racingPostPicks = new EventEmitter<ForecastParticipantViewModel[]>();

    participantModel: ForecastParticipantViewModel[];
    forecastModel: ForecastParticipantViewModel[] = [];
    tricastModel: ForecastParticipantViewModel[] = [];
    isForecastAvailable: boolean;
    isTricastAvailable: boolean;
    postPickModel: ForecastParticipantViewModel[] = [];
    participantPickType = ParticipantPickType;

    constructor(
        protected racingService: RacingService,
        protected horsesFixturePricingHelper: HorsesFixturePricingHelper,
        protected horsesFixtureMarketHelper: HorsesFixtureMarketHelper,
        protected greyhoundsPostPicksService: GreyhoundsPostPicksService,
        protected horsesFixtureParticipantHelper: HorsesFixtureParticipantHelper,
    ) {}

    ngOnChanges(changes: ISimpleChanges<RacingPostBaseComponent>): void {
        if (changes.fixture && changes.fixture.currentValue) {
            this.participantModel = this.buildParticipants(changes.fixture.currentValue);
            if (!changes.fixture.firstChange && changes.fixture.currentValue?.id !== changes.fixture.previousValue?.id) {
                this.forecastModel = [];
                this.tricastModel = [];
            }
            if (changes.fixture.currentValue.racingpost?.postPick) {
                this.getPicksModel(changes.fixture.currentValue.racingpost?.postPick, this.participantModel);
            }
        }

        this.racingPostPicks.emit(this.postPickModel);
    }

    private getPicksModel(postPick: string, participantModel: ForecastParticipantViewModel[]) {
        this.postPickModel = [];
        postPick.split(',').forEach((str) => {
            const postPickModel = participantModel.find((x) => x.participantId === parseInt(str) && x.properties?.type !== ParticipantType.Favourite);
            if (postPickModel) {
                this.postPickModel.push(postPickModel);
            }
        });
        this.forecastModel = cloneDeep(this.postPickModel).splice(0, 2);
        this.tricastModel = this.postPickModel.length === 3 ? cloneDeep(this.postPickModel).splice(0, 3) : [];
    }

    private isParticipantVisible(participant: WinGameParticipant): boolean {
        const option = this.horsesFixturePricingHelper.getPriceForMarketType(participant, ParticipantMarketType.Forecast)[0];
        if (!this.isTricastAvailable) {
            return !!option;
        }

        const tricastOption = this.horsesFixturePricingHelper.getPriceForMarketType(participant, ParticipantMarketType.Tricast)[0];

        return !!option || !!tricastOption;
    }

    isButtonDisabled(isTricast: boolean, isSports: boolean): boolean | undefined {
        if (!isSports && this.fixture.isOptionBet) {
            this.optionGroup = this.getOptionGroup(isTricast ? RacingOptionMarketTypes.Tricast : RacingOptionMarketTypes.Forecast);
            return this.isOptionSuspended(this.optionGroup);
        } else {
            const model = isTricast ? this.tricastModel : this.forecastModel;
            let isModelSuspended = this.racingService.isMarketSuspended(
                model,
                isTricast ? ParticipantMarketType.Tricast : ParticipantMarketType.Forecast,
                this.fixture.isOptionBet,
            );

            const isMarketSuspended = isSports
                ? isModelSuspended
                : isModelSuspended ||
                  this.greyhoundsPostPicksService.isOptionSuspended(
                      model,
                      isTricast ? ParticipantMarketType.Tricast : ParticipantMarketType.Forecast,
                  );

            const isForecastLocked = this.isButtonLocked(this.forecastModel);
            const isTricastLocked = this.isButtonLocked(this.tricastModel);

            return isMarketSuspended || (isTricast ? isTricastLocked : isForecastLocked);
        }
    }

    private isButtonLocked(model: ForecastParticipantViewModel[]) {
        return model.some((p) => this.horsesFixtureParticipantHelper.isNonRunner(p));
    }

    private buildParticipants(fixture: WinGameFixture): ForecastParticipantViewModel[] {
        let markets;
        if (fixture.isOptionBet) {
            markets = fixture.optionGroups?.flatMap((p) => p.parameters?.find((q) => q.key === Tv2MarketParameters.MarketType)?.value);
            this.isForecastAvailable = markets.some((m) => m === MarketView.Forecast);
            this.isTricastAvailable = markets.some((m) => m === MarketView.Tricast);
        } else {
            markets = this.horsesFixtureMarketHelper.getMarkets(fixture);
            this.isForecastAvailable = markets.some((m) => m.view === MarketView.Forecast);
            this.isTricastAvailable = markets.some((m) => m.view === MarketView.Tricast);
        }

        const status = this.isOptionSuspended(this.optionGroup);

        return fixture.participants.map((p) => {
            return {
                ...p,
                availableBadges: [],
                participantInfo: this.racingService.getTrapInfo(p),
                visible: this.isParticipantVisible(p),
                marketStatus: status,
            };
        });
    }

    getOptionGroup(marketType: RacingOptionMarketTypes): EventOptionGroup | undefined {
        return this.racingService.getMarketOptionGroup(this.fixture.optionGroups, marketType);
    }

    private isOptionSuspended(optionGroup: EventOptionGroup | undefined): boolean | undefined {
        if (optionGroup !== undefined) {
            return this.optionGroup?.options.some((o) => o.status === OptionStatus.Suspended);
        }
        return false;
    }
}
